import {
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "../../Firestore/nutrition/nutritionPaths";
import {
  sortClientNutritionPlans,
} from "../../Firestore/nutrition/nutritionRecords";

function normalizeClientId(value) {
  return String(value ?? "").trim().toLowerCase();
}

function normalizePlanId(value) {
  return String(value ?? "").trim().toLowerCase();
}

function uniquePlanIds(planIds) {
  return [...new Set((Array.isArray(planIds) ? planIds : []).map(normalizePlanId).filter(Boolean))];
}

export function getEmptyClientAssignmentStage() {
  return {
    archiveClientPlanIds: [],
    restoreClientPlanIds: [],
  };
}

export function getClientAssignmentStage(stagedChangesByClientId, clientId) {
  const normalizedClientId = normalizeClientId(clientId);
  const stagedClientChanges =
    (stagedChangesByClientId &&
      normalizedClientId &&
      stagedChangesByClientId[normalizedClientId]) ||
    getEmptyClientAssignmentStage();

  return {
    archiveClientPlanIds: uniquePlanIds(stagedClientChanges.archiveClientPlanIds),
    restoreClientPlanIds: uniquePlanIds(stagedClientChanges.restoreClientPlanIds),
  };
}

function withClientAssignmentStage(stagedChangesByClientId, clientId, nextStage) {
  const normalizedClientId = normalizeClientId(clientId);
  if (!normalizedClientId) {
    return stagedChangesByClientId ?? {};
  }

  const hasArchiveIds = nextStage.archiveClientPlanIds.length > 0;
  const hasRestoreIds = nextStage.restoreClientPlanIds.length > 0;
  const nextChanges = { ...(stagedChangesByClientId ?? {}) };

  if (!hasArchiveIds && !hasRestoreIds) {
    delete nextChanges[normalizedClientId];
    return nextChanges;
  }

  nextChanges[normalizedClientId] = nextStage;
  return nextChanges;
}

export function removeClientAssignmentStage(stagedChangesByClientId, clientId) {
  return withClientAssignmentStage(
    stagedChangesByClientId,
    clientId,
    getEmptyClientAssignmentStage(),
  );
}

export function applyClientAssignmentStageAction({
  stagedChangesByClientId,
  clientId,
  assignment,
  action,
} = {}) {
  const normalizedPlanId = normalizePlanId(assignment?.client_plan_id);
  if (!normalizedPlanId) {
    return stagedChangesByClientId ?? {};
  }

  const currentStage = getClientAssignmentStage(stagedChangesByClientId, clientId);
  const archiveSet = new Set(currentStage.archiveClientPlanIds);
  const restoreSet = new Set(currentStage.restoreClientPlanIds);
  const originalStatus = String(assignment?.status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED
    ? CLIENT_NUTRITION_STATUS_ARCHIVED
    : CLIENT_NUTRITION_STATUS_ACTIVE;

  if (action === "archive") {
    if (originalStatus === CLIENT_NUTRITION_STATUS_ARCHIVED) {
      restoreSet.delete(normalizedPlanId);
    } else {
      archiveSet.add(normalizedPlanId);
      restoreSet.delete(normalizedPlanId);
    }
  }

  if (action === "restore") {
    if (originalStatus === CLIENT_NUTRITION_STATUS_ARCHIVED) {
      restoreSet.add(normalizedPlanId);
      archiveSet.delete(normalizedPlanId);
    } else {
      archiveSet.delete(normalizedPlanId);
      restoreSet.delete(normalizedPlanId);
    }
  }

  return withClientAssignmentStage(stagedChangesByClientId, clientId, {
    archiveClientPlanIds: [...archiveSet],
    restoreClientPlanIds: [...restoreSet],
  });
}

export function deriveClientAssignmentSections(assignments, stagedClientChanges = {}) {
  const archivePlanIdSet = new Set(uniquePlanIds(stagedClientChanges.archiveClientPlanIds));
  const restorePlanIdSet = new Set(uniquePlanIds(stagedClientChanges.restoreClientPlanIds));

  const projectedAssignments = sortClientNutritionPlans(
    (Array.isArray(assignments) ? assignments : []).map((assignment) => {
      const assignmentId = normalizePlanId(assignment?.client_plan_id);
      const originalStatus = String(assignment?.status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED
        ? CLIENT_NUTRITION_STATUS_ARCHIVED
        : CLIENT_NUTRITION_STATUS_ACTIVE;

      if (originalStatus === CLIENT_NUTRITION_STATUS_ACTIVE && archivePlanIdSet.has(assignmentId)) {
        return {
          ...assignment,
          status: CLIENT_NUTRITION_STATUS_ARCHIVED,
          isPendingArchive: true,
          isPendingRestore: false,
        };
      }

      if (originalStatus === CLIENT_NUTRITION_STATUS_ARCHIVED && restorePlanIdSet.has(assignmentId)) {
        return {
          ...assignment,
          status: CLIENT_NUTRITION_STATUS_ACTIVE,
          isPendingArchive: false,
          isPendingRestore: true,
        };
      }

      return {
        ...assignment,
        status: originalStatus,
        isPendingArchive: false,
        isPendingRestore: false,
      };
    }),
  );

  return projectedAssignments.reduce(
    (accumulator, assignment) => {
      if (assignment.status === CLIENT_NUTRITION_STATUS_ARCHIVED) {
        accumulator.archived.push(assignment);
        return accumulator;
      }

      accumulator.active.push(assignment);
      return accumulator;
    },
    { active: [], archived: [] },
  );
}

export function getSelectedClientRowBadges({
  selectedClientIds,
  clientId,
  stagedChangesByClientId,
  assignments,
} = {}) {
  const normalizedClientId = normalizeClientId(clientId);
  const selectedIdSet = new Set(
    (Array.isArray(selectedClientIds) ? selectedClientIds : []).map(normalizeClientId).filter(Boolean),
  );
  const stagedClientChanges = getClientAssignmentStage(stagedChangesByClientId, normalizedClientId);
  const persistedAssignments = Array.isArray(assignments) ? assignments : null;
  const optimisticSelectedAddCount = selectedIdSet.has(normalizedClientId) ? 1 : 0;

  if (!persistedAssignments) {
    return {
      isReady: true,
      addCount: optimisticSelectedAddCount,
      removalCount: 0,
    };
  }

  const persistedStatusByPlanId = persistedAssignments.reduce((statusByPlanId, assignment) => {
    const normalizedPlanId = normalizePlanId(
      assignment?.client_plan_id ?? assignment?.id,
    );
    if (!normalizedPlanId) {
      return statusByPlanId;
    }

    statusByPlanId[normalizedPlanId] =
      String(assignment?.status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED
        ? CLIENT_NUTRITION_STATUS_ARCHIVED
        : CLIENT_NUTRITION_STATUS_ACTIVE;
    return statusByPlanId;
  }, {});

  const restoreAddCount = stagedClientChanges.restoreClientPlanIds.reduce((count, planId) => {
    return persistedStatusByPlanId[planId] === CLIENT_NUTRITION_STATUS_ARCHIVED
      ? count + 1
      : count;
  }, 0);

  const archiveRemovalCount = stagedClientChanges.archiveClientPlanIds.reduce((count, planId) => {
    return persistedStatusByPlanId[planId] === CLIENT_NUTRITION_STATUS_ACTIVE
      ? count + 1
      : count;
  }, 0);

  return {
    isReady: true,
    addCount: optimisticSelectedAddCount + restoreAddCount,
    removalCount: archiveRemovalCount,
  };
}

export function hasPendingAssignmentChanges({
  selectedClientIds,
  stagedChangesByClientId,
} = {}) {
  if ((Array.isArray(selectedClientIds) ? selectedClientIds : []).length > 0) {
    return true;
  }

  return Object.values(stagedChangesByClientId ?? {}).some((stage) => {
    const archiveClientPlanIds = uniquePlanIds(stage?.archiveClientPlanIds);
    const restoreClientPlanIds = uniquePlanIds(stage?.restoreClientPlanIds);

    return archiveClientPlanIds.length > 0 || restoreClientPlanIds.length > 0;
  });
}
